/**
 * Process multipart/x-mixed-replace stream for Salient H264 video
 * Handles stream format similar to MJPEG but with different content types
 */
export function processMultipartStream(reader, boundary, frameCallback, onStreamEnd, isPausedRef) {
	if (!reader) {
		return;
	}

	let buffer = new Uint8Array(0);
	let isPumpScheduled = false;
	let state = 'boundary'; // 'boundary', 'headers', 'content'
	let currentHeaders = {};
	let contentLength = 0;
	let contentRead = 0;
	let contentBuffer = new Uint8Array(0); // Buffer to accumulate content chunks

	// Helper function to concatenate Uint8Arrays
	function concatUint8Arrays(a, b) {
		const result = new Uint8Array(a.length + b.length);
		result.set(a);
		result.set(b, a.length);
		return result;
	}

	// Helper function to convert Uint8Array to string
	function uint8ArrayToString(arr) {
		return new TextDecoder().decode(arr);
	}

	// Helper function to find pattern in buffer
	function findPattern(buffer, pattern) {
		const patternBytes = new TextEncoder().encode(pattern);
		for (let i = 0; i <= buffer.length - patternBytes.length; i++) {
			let match = true;
			for (let j = 0; j < patternBytes.length; j++) {
				if (buffer[i + j] !== patternBytes[j]) {
					match = false;
					break;
				}
			}
			if (match) {
				return i;
			}
		}
		return -1;
	}

	// Helper function to parse headers
	function parseHeaders(headerString) {
		const headers = {};
		const lines = headerString.split('\r\n');

		for (const line of lines) {
			const colonIndex = line.indexOf(':');
			if (colonIndex !== -1) {
				const key = line.substring(0, colonIndex).trim().toLowerCase();
				const value = line.substring(colonIndex + 1).trim();
				headers[key] = value;
			}
		}

		return headers;
	}

	function pump() {
		if (isPausedRef.current) {
			if (!isPumpScheduled) {
				isPumpScheduled = true;
				setTimeout(() => {
					isPumpScheduled = false;
					pump();
				}, 100);
			}
			return;
		}

		reader
			.read()
			.then(function (result) {
				if (result.done) {
					onStreamEnd();
					return;
				}

				// Append new data to buffer
				buffer = concatUint8Arrays(buffer, result.value);

				// Process buffer based on current state
				while (buffer.length > 0) {
					if (state === 'boundary') {
						// Look for boundary
						const boundaryPattern = `--${boundary}`;
						const boundaryIndex = findPattern(buffer, boundaryPattern);

						if (boundaryIndex !== -1) {
							// Found boundary, remove everything before it
							const boundaryEnd = boundaryIndex + boundaryPattern.length;

							// Check if this is the end boundary
							if (buffer.length > boundaryEnd + 2) {
								const next2Chars = uint8ArrayToString(buffer.slice(boundaryEnd, boundaryEnd + 2));
								if (next2Chars === '--') {
									// End boundary found
									onStreamEnd();
									return;
								}
							}

							// Remove everything up to and including the boundary
							buffer = buffer.slice(boundaryEnd);

							// Skip CRLF after boundary
							if (buffer.length >= 2 && buffer[0] === 13 && buffer[1] === 10) {
								buffer = buffer.slice(2);
							}

							state = 'headers';
							currentHeaders = {};
							contentLength = 0;
							contentRead = 0;
							contentBuffer = new Uint8Array(0);
						} else {
							// No boundary found, keep some data for next iteration
							if (buffer.length > 1024) {
								buffer = buffer.slice(buffer.length - 1024);
							}
							break;
						}
					} else if (state === 'headers') {
						// Look for end of headers (double CRLF)
						const headerEndIndex = findPattern(buffer, '\r\n\r\n');

						if (headerEndIndex !== -1) {
							// Extract headers
							const headerString = uint8ArrayToString(buffer.slice(0, headerEndIndex));
							currentHeaders = parseHeaders(headerString);

							// Remove headers from buffer
							buffer = buffer.slice(headerEndIndex + 4);

							// Get content length
							contentLength = parseInt(currentHeaders['content-length'] || '0', 10);
							contentRead = 0;
							contentBuffer = new Uint8Array(0);

							if (contentLength > 0) {
								state = 'content';
							} else {
								// No content, go back to looking for boundary
								state = 'boundary';
							}
						} else {
							// Headers not complete, need more data
							break;
						}
					} else if (state === 'content') {
						// Read content
						const remainingContent = contentLength - contentRead;
						const availableData = Math.min(remainingContent, buffer.length);

						if (availableData > 0) {
							const contentChunk = buffer.slice(0, availableData);

							// Accumulate content chunks
							contentBuffer = concatUint8Arrays(contentBuffer, contentChunk);
							contentRead += availableData;
							buffer = buffer.slice(availableData);

							// If we have all the content, process it
							if (contentRead === contentLength) {
								// Check for Content-Length mismatch
								if (contentBuffer.length !== contentLength) {
									state = 'boundary';
									currentHeaders = {};
									contentLength = 0;
									contentRead = 0;
									contentBuffer = new Uint8Array(0);
									break;
								}

								// Process the frame
								frameCallback({
									headers: currentHeaders,
									data: contentBuffer,
									contentType: currentHeaders['content-type'] || '',
									syncPoint: currentHeaders['sync-point'] === 'yes',
									preRoll: currentHeaders['pre-roll'] === 'yes',
									mediaStart: currentHeaders['media-start'],
									mediaEnd: currentHeaders['media-end'],
									streamStart: currentHeaders['stream-start'],
									streamEnd: currentHeaders['stream-end'],
									endOfStream: currentHeaders['end-of-stream'] === 'yes',
									cameraStatus: parseInt(currentHeaders['camera-status'] || '0', 10),
								});

								state = 'boundary';
								currentHeaders = {};
								contentLength = 0;
								contentRead = 0;
								contentBuffer = new Uint8Array(0);
							}
						} else {
							// Need more data
							break;
						}
					}
				}

				// Continue reading
				pump();
			})
			.catch((error) => {
				onStreamEnd();
			});
	}

	// Start processing
	pump();
}
